import { ArrowForward, Edit, VisibilityOff } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dish, MenuCategory } from '../../../apiTypes';
import DataTable, { Column } from '../../../components/DataTable';
import Image from '../../../components/Image';
import PrimaryButton from '../../../components/PrimaryButton';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import { createBatch } from '../../../utils/api';

import MenuBreadcrumbs from '../MenuBreadcrumbs/MenuBreadcrumbs';
import EditCategory from './EditCategory';

type Row = DbDocument<MenuCategory> & {
  activeItemsCount: number;
  hiddenItemsCount: number;
};

type Params = {
  sectionId: string;
};

const CategoriesList = () => {
  const { lang } = useLocale();
  const navigate = useNavigate();
  const { cafeSlug } = useAdmin();
  const { categories, dishes, sections } = useCafe();
  const { sectionId } = useParams<Params>();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<DbDocument<MenuCategory> | null>(null);

  const section = useMemo(() => sections.find((s) => s.id === sectionId), [sectionId, sections]);

  const filteredCategories = useMemo(
    () => categories.filter((c) => c.sectionId === sectionId),
    [categories, sectionId]
  );

  const dishesByCategory = useMemo(() => {
    const dishesByCategory: { [key in string]: DbDocument<Dish>[] } = {};
    dishes.forEach((dish) => {
      if (dishesByCategory[dish.categoryId]) {
        dishesByCategory[dish.categoryId]?.push(dish);
      } else {
        dishesByCategory[dish.categoryId] = [dish];
      }
    });
    return dishesByCategory;
  }, [dishes]);

  const data: Row[] = useMemo(
    () =>
      filteredCategories.map((category) => ({
        ...category,
        activeItemsCount: dishesByCategory[category.id]?.filter((c) => c.show)?.length || 0,
        hiddenItemsCount: dishesByCategory[category.id]?.filter((c) => !c.show)?.length || 0,
      })),
    [dishesByCategory, filteredCategories]
  );

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'imageURL',
        title: '',
        render: (r: Row) => <Image src={r.imageUrl} />,
        sx: { width: 40, pr: 0 },
      },
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ color: (t) => (r.show ? t.palette.text.primary : t.palette.text.disabled) }}>
              {r.name}
            </Typography>{' '}
            {r.show ? '' : <VisibilityOff fontSize="small" color="disabled" sx={{ ml: 1 }} />}
          </Box>
        ),
      },
      {
        name: 'description',
        title: t.description[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.description}</Typography>,
        hideOnMobile: true,
      },
      {
        name: 'active_items',
        title: t.active_items[lang],
        render: (r: Row) => <Typography>{r.activeItemsCount}</Typography>,
        hideOnMobile: true,
      },
      {
        name: 'edit',
        title: '',
        render: (r: Row) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setCategory(r);
              setOpen(true);
            }}
          >
            <Edit />
          </IconButton>
        ),
      },
      {
        name: 'goTo',
        title: '',
        render: (r: Row) => (
          <IconButton>
            <ArrowForward />
          </IconButton>
        ),
      },
    ],
    [lang]
  );

  const handleOrderChange = useCallback(
    async (row: Row, direction: 'up' | 'down') => {
      const index = filteredCategories.findIndex((section) => section.id === row.id);
      const secondIndex = direction === 'up' ? index - 1 : index + 1;
      const first = filteredCategories[index];
      const second = filteredCategories[secondIndex];

      if (first && second) {
        const batch = createBatch();
        batch.update<MenuCategory>(`cafes/${cafeSlug}/categories/${first.id}`, { order: second.order });
        batch.update<MenuCategory>(`cafes/${cafeSlug}/categories/${second.id}`, { order: first.order });
        await batch.commit();
      }
    },
    [cafeSlug, filteredCategories]
  );

  useEffect(() => {
    if (!section) {
      navigate(`/${cafeSlug}`);
    }
  }, [cafeSlug, navigate, section]);

  if (!section) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 1200, m: '0 auto' }}>
      <MenuBreadcrumbs />

      <DataTable
        columns={columns}
        rows={data}
        onRowClick={(row) => navigate(`/${cafeSlug}/menu/${sectionId}/${row.id}`)}
        onOrderChange={handleOrderChange}
        dataTestIdPrefix="category"
        actionButton={
          <PrimaryButton onClick={() => setOpen(true)} variant="contained">
            {t.add_category[lang]}
          </PrimaryButton>
        }
      />

      {open && (
        <EditCategory
          category={category}
          lastIndex={filteredCategories[filteredCategories.length - 1]?.order || 0}
          open={open}
          sectionId={section.id}
          onClose={() => {
            setOpen(false);
            setCategory(null);
          }}
        />
      )}
    </Box>
  );
};

export default CategoriesList;
